<template>
    <v-dialog
        v-model="show_dialog"
        max-width="1400"
        scrollable
    >
        <v-card>
            <v-card-title class="headline">
                All Possible Automated Messages
            </v-card-title>
            <v-card-text>
                <v-data-table
                    :headers="headers"
                    :items="formatted_contexts"
                    :items-per-page="100"
                    dense
                    :hide-default-footer="formatted_contexts.length <= 100"
                >
                    <template v-slot:item.context="{ item }">
                        <div class="font-weight-medium">
                            {{ item.display_label }}
                        </div>
                        <div class="text-caption grey--text">
                            {{ item.template_context }}
                        </div>
                    </template>

                    <template v-slot:item.is_deprecated="{ item }">
                        <!-- ON hover explain this means they're no longer creatable -->
                        <span v-if="item.is_deprecated" class="deprecated-indicator">
                            Deprecated
                            <v-tooltip top>
                                <template v-slot:activator="{ on, attrs }">
                                    <v-icon
                                        v-bind="attrs"
                                        v-on="on"
                                        small
                                        color="orange"
                                        class="ml-1"
                                    >
                                        mdi-help-circle-outline
                                    </v-icon>
                                </template>
                                <span>This automated message is no longer creatable but may still exist for historical reasons.</span>
                            </v-tooltip>
                        </span>
                        <span v-else class="text--secondary">Active</span>
                    </template>

                    <template v-slot:item.restrictions="{ item }">
                        <div class="d-flex flex-column">
                            <v-tooltip
                                v-if="item.staff_only"
                                top
                                color="red darken-1"
                                :disabled="!staffTooltip"
                            >
                                <template v-slot:activator="{ on, attrs }">
                                    <v-chip
                                        v-bind="attrs"
                                        v-on="on"
                                        small
                                        pill
                                        color="red lighten-4"
                                        text-color="red darken-2"
                                        class="mb-2 align-self-start"
                                    >
                                        Staff Only
                                    </v-chip>
                                </template>
                                <span>Only RedChirp staff can create this automated message.</span>
                            </v-tooltip>
                            <template v-if="item.integrations.length > 1">
                                <div class="chip-row">
                                    <v-tooltip top>
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-chip
                                                v-bind="attrs"
                                                v-on="on"
                                                color="indigo lighten-5"
                                                text-color="indigo darken-3"
                                                small
                                                pill
                                                class="mr-2"
                                                style="cursor: help"
                                            >
                                                {{ item.integrations.length }} Integrations
                                            </v-chip>
                                        </template>
                                        <div class="pa-2">
                                            <div class="font-weight-bold mb-1">Supported Integrations:</div>
                                            <div v-for="restriction in item.integrations" :key="restriction.code" class="ml-2">
                                                • {{ restriction.label }}
                                            </div>
                                        </div>
                                    </v-tooltip>
                                </div>
                            </template>
                            <template v-else>
                                <div
                                    v-for="restriction in item.integrations"
                                    :key="`${item.template_context}-${restriction.code}`"
                                    class="chip-row"
                                >
                                    <v-tooltip top>
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-chip
                                                v-bind="attrs"
                                                v-on="on"
                                                :color="getRestrictionChipColor(restriction)"
                                                :text-color="getRestrictionChipTextColor(restriction)"
                                                small
                                                pill
                                                class="mr-2"
                                            >
                                                {{ restriction.label }}
                                            </v-chip>
                                        </template>
                                        <span>{{ restriction.description }}</span>
                                    </v-tooltip>
                                </div>
                            </template>
                            <span
                                v-if="!item.integrations.length && !item.staff_only"
                                class="text--secondary"
                            >
                                No restrictions
                            </span>
                        </div>
                    </template>
                </v-data-table>
            </v-card-text>
            <v-card-actions>
                <v-spacer />
                <v-btn
                    text
                    color="primary"
                    @click="close"
                >
                    Close
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>

export default {
    data() {
        return {
            show_dialog: false,
            contexts: [],
            headers: [
                { text: 'Message Type', value: 'context' },
                { text: 'Deprecated', value: 'is_deprecated', sortable: false },
                { text: 'Restrictions', value: 'restrictions', sortable: false },
            ],
            staffTooltip: true,
        };
    },
    computed: {
        formatted_contexts() {
            if (!this.contexts) return [];

            return this.contexts
                .map(context => {
                    const restrictions = context.restrictions || [];
                    const integrations = this.getIntegrationRestrictions(restrictions).map(restriction => ({
                        ...restriction,
                        description: restriction.description || 'Available with this integration',
                    }));

                    return {
                        template_context: context.template_context,
                        display_label: this.formatLabel(context),
                        integrations,
                        staff_only: this.hasStaffRestriction(restrictions),
                        is_deprecated: context.is_deprecated === true,
                    };
                })
                .sort((a, b) => a.display_label.localeCompare(b.display_label));
        },
    },
    methods: {
        show(contexts) {
            this.contexts = contexts || [];
            this.show_dialog = true;
        },
        close() {
            this.show_dialog = false;
        },
        formatLabel(context) {
            if (!context) return '';

            if (context.context_code) {
                return this.prettyCode(context.context_code);
            }

            if (context.template_context_enum) {
                return this.prettyCode(context.template_context_enum.replace(/_/g, '-').toLowerCase());
            }

            return this.prettyCode(context.template_context);
        },
        prettyCode(code) {
            if (!code) return '';
            return code
                .split('-')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ');
        },
        getIntegrationRestrictions(restrictions) {
            return restrictions.filter(restriction => restriction.type === 'integration');
        },
        hasStaffRestriction(restrictions) {
            return restrictions.some(restriction => restriction.type === 'staff-only');
        },
        getRestrictionChipColor(restriction) {
            if (!restriction) return 'grey lighten-4';

            if (restriction.type === 'staff-only') {
                return 'red lighten-4';
            }

            if (restriction.type === 'integration') {
                return 'indigo lighten-5';
            }

            return 'grey lighten-4';
        },
        getRestrictionChipTextColor(restriction) {
            if (!restriction) return 'grey darken-2';

            if (restriction.type === 'staff-only') {
                return 'red darken-2';
            }

            if (restriction.type === 'integration') {
                return 'indigo darken-3';
            }

            return 'grey darken-2';
        },
    },
};
</script>

<style lang="scss" scoped>
.restriction-chip-group {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    max-height: 120px;
    overflow-y: auto;
    padding-right: 4px;
}

.staff-pill {
    display: flex;
}

.deprecated-indicator {
    display: flex;
    align-items: center;
    font-weight: 500;
    color: #ff9800;
}
</style>
